/**
 * /_geminii/admin — Geminii super-admin console.
 *
 * Tenant list, search, suspend/resume, module toggles, impersonate.
 * Audit-logged on the backend. Restricted to geminii_admin role.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Shield, Loader2, PauseCircle, PlayCircle, UserCheck,
  ChevronRight, ChevronDown, AlertTriangle, RefreshCw, ScrollText,
  Globe, Plus, X, Palette, Database, Repeat, Check,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_TINT = {
  trial: "text-amber-300 bg-amber-400/10",
  active: "text-emerald-300 bg-emerald-400/10",
  suspended: "text-rose-300 bg-rose-400/10",
  cancelled: "text-[#647087] bg-white/[0.04]",
};

function DomainsEditor({ tenant, onUpdated, tenantSuffix, scheme, baseDomain, productLine }) {
  const [host, setHost] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [probeResult, setProbeResult] = useState(null);
  const [dnsBusy, setDnsBusy] = useState(false);
  const [dnsResult, setDnsResult] = useState(null);
  const domains = tenant.custom_domains || [];
  const _slug = tenant.slug || tenant.id;
  const _product = productLine || "rogue";
  const _base = baseDomain || "geminii-ai.com";
  // New hyphen scheme is the default (free SSL covers it).
  const implicitHost = (scheme === "subdomain")
    ? `${_slug}.${_product}.${_base}`
    : `${_slug}-${_product}.${_base}`;

  const add = async (e) => {
    e?.preventDefault?.();
    if (!host.trim()) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/_geminii/admin/tenants/${tenant.id}/domains`, {
        host: host.trim().toLowerCase(),
      });
      setHost("");
      onUpdated?.({ ...tenant, custom_domains: data.custom_domains });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to attach domain.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (h) => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/_geminii/admin/tenants/${tenant.id}/domains/${encodeURIComponent(h)}`);
      onUpdated?.({ ...tenant, custom_domains: data.custom_domains });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to remove domain.");
    } finally {
      setBusy(false);
    }
  };

  const probe = async (h) => {
    setProbeResult(null);
    try {
      const { data } = await api.get(`/_geminii/admin/tenants/${tenant.id}/domains/probe`, { params: { host: h } });
      setProbeResult(data);
    } catch (e) {
      setProbeResult({ error: e?.response?.data?.detail || "Probe failed" });
    }
  };

  return (
    <div className="mt-4 px-4 py-3 rounded bg-black/30 border border-white/5" data-testid={`tenant-domains-${tenant.id}`}>
      <div className="text-xs uppercase tracking-[0.18em] text-[#1FB6C1] mb-2 inline-flex items-center gap-1.5">
        <Globe className="w-3.5 h-3.5" /> Custom domains
      </div>
      {/* Implicit subdomain — always present, can't be removed */}
      <div className="text-[11px] text-[#647087] mb-2">
        Implicit (always live):{" "}
        <code className="text-[#dde3ec]">{implicitHost}</code>
        <button
          type="button"
          onClick={() => probe(implicitHost)}
          className="ml-2 text-[10px] underline text-[#1FB6C1]/80 hover:text-[#1FB6C1]"
          data-testid={`probe-implicit-${tenant.id}`}
        >
          probe
        </button>
      </div>

      {/* Explicit custom domains */}
      {domains.length === 0 && (
        <div className="text-[11px] text-[#647087] italic">No custom domains attached.</div>
      )}
      {domains.map((d) => (
        <div key={d} className="flex items-center justify-between text-xs py-1.5 border-t border-white/5">
          <code className="text-[#dde3ec]">{d}</code>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => probe(d)}
              className="text-[10px] underline text-[#1FB6C1]/80 hover:text-[#1FB6C1]"
              data-testid={`probe-${tenant.id}-${d}`}
            >
              probe
            </button>
            <button
              type="button"
              onClick={() => remove(d)}
              className="text-[10px] inline-flex items-center gap-1 text-rose-300 hover:text-rose-200"
              data-testid={`remove-domain-${tenant.id}-${d}`}
              disabled={busy}
            >
              <X className="w-3 h-3" /> remove
            </button>
          </div>
        </div>
      ))}

      <form onSubmit={add} className="flex items-center gap-2 mt-3">
        <input
          type="text"
          value={host}
          onChange={(e) => setHost(e.target.value)}
          placeholder="yourcompany.com or crm.yourcompany.com"
          className="hv-input text-xs flex-1 min-w-0"
          data-testid={`add-domain-input-${tenant.id}`}
        />
        <button
          type="submit"
          className="hv-btn hv-btn-primary text-xs inline-flex items-center gap-1"
          disabled={busy || !host.trim()}
          data-testid={`add-domain-btn-${tenant.id}`}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Attach
        </button>
      </form>
      {err && <div className="text-[10px] text-rose-300 mt-1.5">{err}</div>}

      {/* Cloudflare DNS re-provision */}
      <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between gap-2">
        <div className="text-[10px] text-[#647087]">
          Cloudflare DNS: ensures <code className="text-[#dde3ec]">{implicitHost}</code> CNAME exists.
        </div>
        <button
          type="button"
          disabled={dnsBusy}
          onClick={async () => {
            setDnsBusy(true);
            setDnsResult(null);
            try {
              const { data } = await api.post(`/_geminii/admin/tenants/${tenant.id}/provision-dns`);
              setDnsResult(data);
            } catch (e) {
              setDnsResult({ ok: false, error: e?.response?.data?.detail || "Failed" });
            } finally {
              setDnsBusy(false);
            }
          }}
          className="hv-btn hv-btn-ghost text-[10px] inline-flex items-center gap-1"
          data-testid={`provision-dns-${tenant.id}`}
        >
          {dnsBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Globe className="w-3 h-3" />}
          Re-provision DNS
        </button>
      </div>
      {dnsResult && (
        <div
          className={`text-[10px] mt-1.5 px-2 py-1 rounded ${dnsResult.ok ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" : "bg-rose-500/10 border border-rose-500/30 text-rose-300"}`}
        >
          {dnsResult.ok
            ? `✓ CNAME ${dnsResult.status}: ${dnsResult.hostname} → ${dnsResult.target}`
            : `✗ ${dnsResult.error}`}
        </div>
      )}

      {probeResult && (
        <div
          className={`text-[10px] mt-2 px-2 py-1.5 rounded ${probeResult.would_route_correctly ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" : "bg-amber-500/10 border border-amber-500/30 text-amber-300"}`}
          data-testid={`probe-result-${tenant.id}`}
        >
          {probeResult.advice || probeResult.error}
        </div>
      )}
    </div>
  );
}

export default function GeminiAdmin() {
  const nav = useNavigate();
  const { user, setUser } = useAuth();
  const [tenants, setTenants] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState(null);
  const [healthOpen, setHealthOpen] = useState(false);
  const [health, setHealth] = useState(null);
  const [parity, setParity] = useState(null);
  const [healthBusy, setHealthBusy] = useState(false);
  const [expandedDomains, setExpandedDomains] = useState({});
  // Stage 2.5 — brand library + R2 migration + reset demo (inline, no modal)
  const [brandLibOpen, setBrandLibOpen] = useState(false);
  const [brandLib, setBrandLib] = useState(null);
  const [r2Open, setR2Open] = useState(false);
  const [r2Result, setR2Result] = useState(null);
  const [r2Busy, setR2Busy] = useState(false);
  // Iter 419.p — Tenant-id backfill inline panel.
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillConfirming, setBackfillConfirming] = useState(false);

  // Iter 419.r — Tenant reclaim (per-tenant DB copy) inline panel.
  const [reclaimOpen, setReclaimOpen] = useState(false);
  const [reclaimResult, setReclaimResult] = useState(null);
  const [reclaimBusy, setReclaimBusy] = useState(false);
  const [reclaimConfirming, setReclaimConfirming] = useState(false);

  const runBackfill = async (apply) => {
    setBackfillBusy(true);
    setBackfillResult(null);
    try {
      const { data } = await api.post(
        `/_geminii/admin/tenant-id-backfill?apply=${apply}&tenant=hidroverse`,
      );
      setBackfillResult(data);
      setBackfillConfirming(false);
    } catch (e) {
      setBackfillResult({ error: e?.response?.data?.detail || "Backfill failed." });
    } finally {
      setBackfillBusy(false);
    }
  };

  const runReclaim = async (apply) => {
    setReclaimBusy(true);
    setReclaimResult(null);
    try {
      const { data } = await api.post(
        `/_geminii/admin/tenant-db-reclaim?apply=${apply}`,
        {},
        { timeout: 300000 }, // 5 min for real migrations
      );
      setReclaimResult(data);
      setReclaimConfirming(false);
    } catch (e) {
      setReclaimResult({ error: e?.response?.data?.detail || "Reclaim failed." });
    } finally {
      setReclaimBusy(false);
    }
  };
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoStatus, setDemoStatus] = useState("");
  const [demoBusy, setDemoBusy] = useState(false);
  // Cloudflare DNS panel
  const [cfOpen, setCfOpen] = useState(false);
  const [cfStatus, setCfStatus] = useState(null);
  const [cfBusy, setCfBusy] = useState(false);
  const [cfProvisionResult, setCfProvisionResult] = useState(null);

  const loadBrandLib = async () => {
    setBrandLib(null);
    try {
      const { data } = await api.get("/_geminii/admin/brand-library");
      setBrandLib(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load brand library.");
    }
  };
  const toggleBrandLib = async () => {
    const next = !brandLibOpen;
    setBrandLibOpen(next);
    if (next && !brandLib) await loadBrandLib();
  };
  const runR2 = async (apply) => {
    if (apply && !window.confirm("Apply R2 key-prefix backfill to the production DB? Idempotent but mutates rows.")) return;
    setR2Busy(true);
    setR2Result(null);
    try {
      const { data } = await api.post(`/_geminii/admin/r2-migration/phase5?apply=${apply}`);
      setR2Result({ phase: 5, ...data });
    } catch (e) {
      setR2Result({ phase: 5, error: e?.response?.data?.detail || "Phase 5 failed." });
    } finally {
      setR2Busy(false);
    }
  };
  const runR2Verify = async () => {
    setR2Busy(true);
    setR2Result(null);
    try {
      const { data } = await api.post("/_geminii/admin/r2-migration/phase6-verify");
      setR2Result({ phase: 6, ...data });
    } catch (e) {
      setR2Result({ phase: 6, error: e?.response?.data?.detail || "Phase 6 failed." });
    } finally {
      setR2Busy(false);
    }
  };
  const resetDemo = async () => {
    if (!window.confirm("Re-seed the demo tenant? Wipes the demo's business data and rebuilds 15 customers / 80 leads / 120 JCs.")) return;
    setDemoBusy(true);
    setDemoStatus("Starting…");
    try {
      const { data } = await api.post("/_geminii/admin/reset-demo");
      setDemoStatus(data.message || "Re-seed started. Check audit log for completion.");
    } catch (e) {
      setDemoStatus("Failed: " + (e?.response?.data?.detail || "unknown error"));
    } finally {
      setDemoBusy(false);
    }
  };
  const ensureDemoBranding = async () => {
    if (!window.confirm("Force the demo tenant's branding back to the public Rogue/Geminii brand? This removes any HidroVerse leakage.")) return;
    setDemoBusy(true);
    setDemoStatus("Applying Rogue branding to demo…");
    try {
      const { data } = await api.post("/_geminii/admin/tenants/demo/ensure-public-branding");
      setDemoStatus(data.ok ? "✓ Demo tenant now branded as Rogue. Refresh the demo URL to verify." : "Failed.");
    } catch (e) {
      setDemoStatus("Failed: " + (e?.response?.data?.detail || "unknown error"));
    } finally {
      setDemoBusy(false);
    }
  };

  const toggleDomains = (id) => setExpandedDomains((m) => ({ ...m, [id]: !m[id] }));
  const updateTenantInline = (updated) => {
    setTenants((cur) => (cur || []).map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
  };

  const openCfPanel = async () => {
    const next = !cfOpen;
    setCfOpen(next);
    if (next && !cfStatus) {
      setCfBusy(true);
      try {
        const { data } = await api.get("/_geminii/admin/cloudflare/status");
        setCfStatus(data);
      } catch (e) {
        setCfStatus({ configured: false, error: e?.response?.data?.detail || "Failed to load" });
      } finally {
        setCfBusy(false);
      }
    }
  };

  const cfProvisionAll = async () => {
    if (!window.confirm("Provision Cloudflare CNAMEs for ALL active/trial tenants? Idempotent — re-runs safely.")) return;
    setCfBusy(true);
    setCfProvisionResult(null);
    try {
      const { data } = await api.post("/_geminii/admin/cloudflare/provision-all");
      setCfProvisionResult(data);
      // Refresh status
      const { data: s } = await api.get("/_geminii/admin/cloudflare/status");
      setCfStatus(s);
    } catch (e) {
      setCfProvisionResult({ error: e?.response?.data?.detail || "Failed" });
    } finally {
      setCfBusy(false);
    }
  };

  useEffect(() => {
    if (user && user.role !== "geminii_admin") {
      nav("/home");
    }
  }, [user, nav]);

  // [Iter 290zp.13z11] Operator-console host gate — compute eligibility
  // here but DEFER the early return until after all hooks have been
  // called (React rules-of-hooks). The actual return lives just before
  // the main JSX below.
  const operatorHosts = (process.env.REACT_APP_OPERATOR_CONSOLE_HOSTS || "")
    .split(",").map((h) => h.trim().toLowerCase()).filter(Boolean);
  const currentHost = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();
  const hostAllowed = operatorHosts.length === 0 || operatorHosts.includes(currentHost);

  const load = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.get("/_geminii/admin/tenants", { params: q ? { q } : {} });
      setTenants(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load tenants.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (hostAllowed) load();
  }, [hostAllowed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the platform's subdomain config once — shown in the header.
  const [domainCfg, setDomainCfg] = useState(null);
  useEffect(() => {
    if (hostAllowed && user?.role === "geminii_admin") {
      api.get("/_geminii/admin/domain-config")
        .then((r) => setDomainCfg(r.data))
        .catch(() => {});
    }
  }, [user?.role, hostAllowed]);

  const suspend = async (tid) => {
    if (!window.confirm(`Suspend tenant "${tid}"? They'll be locked out immediately.`)) return;
    await api.post(`/_geminii/admin/tenants/${tid}/suspend`);
    await load();
  };
  const resume = async (tid) => {
    await api.post(`/_geminii/admin/tenants/${tid}/resume`);
    await load();
  };
  const impersonate = async (tid) => {
    if (!window.confirm(`Impersonate "${tid}"? Every action will be audit-logged.`)) return;
    const { data } = await api.post(`/_geminii/admin/tenants/${tid}/impersonate`);
    localStorage.setItem("hv_token", data.token);
    setUser(data.user);
    nav("/home");
  };
  const openAudit = async () => {
    setAuditOpen(true);
    const { data } = await api.get("/_geminii/admin/audit-log", { params: { limit: 200 } });
    setAudit(data);
  };

  const openHealth = async () => {
    setHealthOpen(true);
    setHealthBusy(true);
    try {
      const [h, p] = await Promise.all([
        api.get("/_geminii/admin/db-health").then((r) => r.data),
        api.get("/_geminii/admin/migration-parity").then((r) => r.data),
      ]);
      setHealth(h);
      setParity(p);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load DB health.");
    } finally {
      setHealthBusy(false);
    }
  };

  const filtered = useMemo(() => tenants || [], [tenants]);

  // [Iter 290zp.13z11] Deferred host-gate early return — placed AFTER
  // all hooks so React's rules-of-hooks ordering is preserved.
  if (!hostAllowed) {
    return (
      <div className="min-h-screen bg-[#0b0f14] text-white flex items-center justify-center" data-testid="geminii-admin-host-gate">
        <div className="text-center">
          <div className="text-6xl font-bold text-[#647087]">404</div>
          <div className="text-sm text-[#647087] mt-2">Not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0f14] text-white p-6 sm:p-10" data-testid="geminii-admin">
      <header className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="inline-flex items-center gap-2 text-xs text-[#1FB6C1] uppercase tracking-[0.2em] mb-2">
            <Shield className="w-3.5 h-3.5" /> Geminii Super-admin
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold">Tenants</h1>
          <p className="text-sm text-[#9aa3b5] mt-1">{filtered.length || 0} tenants in the cluster</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={openCfPanel}
            className="hv-btn hv-btn-ghost inline-flex items-center gap-1.5 text-sm"
            data-testid="admin-cloudflare-btn"
            title="Cloudflare DNS automation — token status, per-tenant CNAME backfill"
          >
            <Globe className="w-4 h-4" /> Cloudflare DNS
          </button>
          <button
            type="button"
            onClick={toggleBrandLib}
            className="hv-btn hv-btn-ghost inline-flex items-center gap-1.5 text-sm"
            data-testid="admin-brand-library-btn"
            title="Visual overview of every tenant's branding"
          >
            <Palette className="w-4 h-4" /> Brand library
          </button>
          <button
            type="button"
            onClick={() => setR2Open((v) => !v)}
            className="hv-btn hv-btn-ghost inline-flex items-center gap-1.5 text-sm"
            data-testid="admin-r2-migration-btn"
            title="R2 storage cutover — DB key prefix backfill + verify"
          >
            <Database className="w-4 h-4" /> R2 migration
          </button>
          <button
            type="button"
            onClick={() => setBackfillOpen((v) => !v)}
            className="hv-btn hv-btn-ghost inline-flex items-center gap-1.5 text-sm"
            data-testid="admin-tenantid-backfill-btn"
            title="Backfill tenant_id on legacy rows — required before flipping TENANT_DB_PER_TENANT"
          >
            <Shield className="w-4 h-4" /> Tenant-id backfill
          </button>
          <button
            type="button"
            onClick={() => setReclaimOpen((v) => !v)}
            className="hv-btn hv-btn-ghost inline-flex items-center gap-1.5 text-sm"
            data-testid="admin-tenant-reclaim-btn"
            title="Copy each tenant's rows into their per-tenant DB — required before flipping TENANT_DB_PER_TENANT"
          >
            <Database className="w-4 h-4" /> Tenant reclaim
          </button>
          <button
            type="button"
            onClick={() => setDemoOpen((v) => !v)}
            className="hv-btn hv-btn-ghost inline-flex items-center gap-1.5 text-sm"
            data-testid="admin-reset-demo-btn"
            title="Wipe + re-seed the public demo tenant"
          >
            <Repeat className="w-4 h-4" /> Demo data
          </button>
          <button
            type="button"
            onClick={openHealth}
            className="hv-btn hv-btn-ghost inline-flex items-center gap-1.5 text-sm"
            data-testid="admin-db-health"
            title="Verify per-tenant DB connectivity & migration parity"
          >
            <Shield className="w-4 h-4" /> DB health
          </button>
          <button
            type="button"
            onClick={openAudit}
            className="hv-btn hv-btn-ghost inline-flex items-center gap-1.5 text-sm"
            data-testid="admin-audit-log"
          >
            <ScrollText className="w-4 h-4" /> Audit log
          </button>
          <button
            type="button"
            onClick={load}
            disabled={busy}
            className="hv-btn hv-btn-ghost inline-flex items-center gap-1.5 text-sm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
        </div>
      </header>

      {/* Platform subdomain config — small info banner */}
      {domainCfg && (
        <div
          className="mb-4 hv-card px-4 py-2 border-[#1FB6C1]/15 flex items-center gap-3 flex-wrap text-xs"
          data-testid="platform-domain-config"
        >
          <Globe className="w-4 h-4 text-[#1FB6C1] shrink-0" />
          <span className="text-[#9aa3b5]">Product line:</span>
          <code className="text-[#dde3ec]">{domainCfg.product_line}</code>
          <span className="text-[#9aa3b5]">·</span>
          <span className="text-[#9aa3b5]">Tenant URL pattern:</span>
          <code className="text-[#dde3ec]">
            {domainCfg.scheme === "subdomain"
              ? `<slug>.${domainCfg.product_line}.${domainCfg.base_domain}`
              : `<slug>-${domainCfg.product_line}.${domainCfg.base_domain}`}
          </code>
          <span className="text-[#9aa3b5]">·</span>
          <span className="text-[#9aa3b5]">Cloudflare DNS:</span>
          <code className={domainCfg.cloudflare_dns_configured ? "text-emerald-300" : "text-amber-300"}>
            {domainCfg.cloudflare_dns_configured ? "automated" : "manual"}
          </code>
        </div>
      )}

      {/* Cloudflare DNS panel — inline, no modal */}
      {cfOpen && (
        <section className="mb-6 hv-card p-5 border-[#1FB6C1]/20" data-testid="cloudflare-dns-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold inline-flex items-center gap-2">
              <Globe className="w-4 h-4 text-[#1FB6C1]" /> Cloudflare DNS automation
            </h2>
            <button
              type="button"
              onClick={() => setCfOpen(false)}
              className="text-xs text-[#9aa3b5] hover:text-white"
              data-testid="cloudflare-dns-close"
            >
              Hide
            </button>
          </div>

          {cfBusy && !cfStatus && (
            <div className="text-xs text-[#9aa3b5] inline-flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading Cloudflare state…
            </div>
          )}

          {cfStatus && (
            <div className="space-y-3 text-xs">
              {cfStatus.configured ? (
                <>
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div>
                      <span className="text-[#647087]">Token status:</span>{" "}
                      <code className="text-emerald-300">{cfStatus.token_status || "—"}</code>
                    </div>
                    <div>
                      <span className="text-[#647087]">Zone:</span>{" "}
                      <code className="text-[#dde3ec]">{cfStatus.zone_name || cfStatus.zone_id}</code>
                    </div>
                    <div>
                      <span className="text-[#647087]">Tenant CNAMEs found:</span>{" "}
                      <code className="text-[#dde3ec]">{cfStatus.tenant_record_count}</code>
                    </div>
                  </div>

                  <div className="max-h-48 overflow-y-auto border border-white/5 rounded">
                    {(cfStatus.tenant_records || []).map((r) => (
                      <div key={r.name} className="px-3 py-1.5 flex items-center justify-between border-b border-white/5 last:border-b-0">
                        <code className="text-[#dde3ec] text-[11px]">{r.name}</code>
                        <span className="text-[#647087] text-[10px]">
                          → {r.content} {r.proxied ? "🟧 proxied" : "DNS only"}
                        </span>
                      </div>
                    ))}
                    {(!cfStatus.tenant_records || cfStatus.tenant_records.length === 0) && (
                      <div className="px-3 py-2 text-[10px] text-[#647087] italic">No tenant CNAMEs yet.</div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <button
                      type="button"
                      onClick={cfProvisionAll}
                      disabled={cfBusy}
                      className="hv-btn hv-btn-primary text-xs inline-flex items-center gap-1.5"
                      data-testid="cf-provision-all-btn"
                    >
                      {cfBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Globe className="w-3 h-3" />}
                      Provision DNS for all tenants
                    </button>
                  </div>

                  {cfProvisionResult && (
                    <div className="mt-2 text-[11px] bg-black/30 border border-white/10 rounded p-3">
                      {cfProvisionResult.error ? (
                        <span className="text-rose-300">{cfProvisionResult.error}</span>
                      ) : (
                        <>
                          <div className="mb-2">
                            <span className="text-emerald-300">✓ {cfProvisionResult.provisioned} succeeded</span>
                            {cfProvisionResult.failed > 0 && (
                              <span className="text-rose-300 ml-3">✗ {cfProvisionResult.failed} failed</span>
                            )}
                          </div>
                          <div className="max-h-40 overflow-y-auto">
                            {(cfProvisionResult.results || []).map((r) => (
                              <div key={r.tenant_id} className="text-[10px] py-0.5">
                                {r.ok ? "✓" : "✗"} <code>{r.tenant_id}</code> · {r.hostname}
                                {r.ok ? ` (${r.status})` : ` — ${r.error}`}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-amber-300 text-[11px]">
                  {cfStatus.error || "CLOUDFLARE_API_TOKEN / CLOUDFLARE_ZONE_ID not configured in backend .env."}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Stage 2.5 — Brand library (inline, no modal) */}
      {brandLibOpen && (
        <section
          className="mb-6 hv-card p-5 border-[#1FB6C1]/20"
          data-testid="brand-library-panel"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold inline-flex items-center gap-2">
              <Palette className="w-4 h-4 text-[#1FB6C1]" /> Brand library
            </h2>
            <button
              type="button"
              onClick={() => setBrandLibOpen(false)}
              className="text-xs text-[#9aa3b5] hover:text-white"
              data-testid="brand-library-close"
            >
              Hide
            </button>
          </div>
          {brandLib === null ? (
            <div className="text-sm text-[#9aa3b5] inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : brandLib.length === 0 ? (
            <div className="text-sm text-[#9aa3b5]">No tenants found.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {brandLib.map((t) => {
                const b = t.branding || {};
                return (
                  <div
                    key={t.id}
                    className="rounded-lg border border-white/5 p-3 bg-black/20 flex gap-3"
                    data-testid={`brand-card-${t.id}`}
                  >
                    <div
                      className="w-16 h-16 rounded shrink-0 border border-white/10 flex items-center justify-center overflow-hidden"
                      style={{ background: b.primary_color || "#0b0f14" }}
                    >
                      {b.logo_url ? (
                        <img src={b.logo_url} alt="" className="max-h-14 max-w-14 object-contain" />
                      ) : (
                        <span className="text-[10px] text-white/60 px-1 text-center">no logo</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">
                        {b.app_name || t.name || t.id}
                      </div>
                      <div className="text-[11px] text-[#9aa3b5] truncate">
                        AI: {b.ai_name || "—"} · plan: {t.plan || "—"}
                      </div>
                      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                        <span className="inline-flex items-center gap-1 text-[10px] text-[#9aa3b5]">
                          <span
                            className="inline-block w-3 h-3 rounded-full border border-white/20"
                            style={{ background: b.primary_color || "#000" }}
                          />
                          {b.primary_color || "—"}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[10px] text-[#9aa3b5]">
                          <span
                            className="inline-block w-3 h-3 rounded-full border border-white/20"
                            style={{ background: b.accent_color || "#000" }}
                          />
                          {b.accent_color || "—"}
                        </span>
                      </div>
                      <div className="text-[10px] text-[#647087] mt-1 truncate">
                        id: <code>{t.id}</code>
                        {t.is_demo && <span className="ml-1 px-1 rounded bg-amber-500/15 text-amber-300">demo</span>}
                      </div>
                      {/* [Iter 290zp.13z10] Quick action: mirror the
                          tenant's main `branding.logo_url` into both
                          theme-specific slots. Saves admins from
                          having to open a Mongo shell when a tenant
                          uploads a single logo and wants it used
                          everywhere. Audit-logged server-side. */}
                      <button
                        type="button"
                        className="hv-btn hv-btn-ghost text-[10px] py-0.5 px-2 mt-2"
                        data-testid={`brand-sync-logos-${t.id}`}
                        title="Copy branding.logo_url into logo_url_dark + logo_url_light"
                        disabled={!b.logo_url}
                        onClick={async () => {
                          if (!b.logo_url) return;
                          if (!window.confirm(
                            `Mirror ${t.id}'s main logo into the dark + light theme slots?\n\nThis is safe — only the three logo fields under branding are changed; everything else stays as is.`
                          )) return;
                          try {
                            await api.post(
                              `/_geminii/admin/tenants/${t.id}/branding/logos`,
                              { sync_from_main: true }
                            );
                            await loadBrandLib();
                          } catch (e) {
                            window.alert(
                              "Logo sync failed: " + (e?.response?.data?.detail || e?.message || "unknown error")
                            );
                          }
                        }}
                      >
                        Sync logos
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Stage 2.5 — R2 migration (Phase 5/6) inline panel */}
      {r2Open && (
        <section
          className="mb-6 hv-card p-5 border-[#1FB6C1]/20"
          data-testid="r2-migration-panel"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold inline-flex items-center gap-2">
              <Database className="w-4 h-4 text-[#1FB6C1]" /> R2 storage cutover
            </h2>
            <button
              type="button"
              onClick={() => setR2Open(false)}
              className="text-xs text-[#9aa3b5] hover:text-white"
              data-testid="r2-migration-close"
            >
              Hide
            </button>
          </div>
          <p className="text-xs text-[#9aa3b5] mb-3">
            Phase 5 backfills the <code>R2_TENANT_PREFIX</code> onto every DB-stored R2 key.
            Phase 6 verifies every DB key still resolves in the bucket. Idempotent and safe to re-run.
          </p>
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              type="button"
              onClick={() => runR2(false)}
              disabled={r2Busy}
              className="hv-btn hv-btn-ghost text-xs inline-flex items-center gap-1"
              data-testid="r2-phase5-dry-run"
            >
              {r2Busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Database className="w-3.5 h-3.5" />}
              Phase 5 — dry-run
            </button>
            <button
              type="button"
              onClick={() => runR2(true)}
              disabled={r2Busy}
              className="hv-btn hv-btn-primary text-xs inline-flex items-center gap-1"
              data-testid="r2-phase5-apply"
            >
              {r2Busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Database className="w-3.5 h-3.5" />}
              Phase 5 — apply
            </button>
            <button
              type="button"
              onClick={runR2Verify}
              disabled={r2Busy}
              className="hv-btn hv-btn-ghost text-xs inline-flex items-center gap-1"
              data-testid="r2-phase6-verify"
            >
              {r2Busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
              Phase 6 — verify
            </button>
          </div>
          {r2Result && (
            <div className="text-xs bg-black/30 border border-white/5 rounded p-3" data-testid="r2-result">
              {r2Result.error ? (
                <div className="text-rose-300 inline-flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5" /> {r2Result.error}
                </div>
              ) : (
                <>
                  <div className="text-[#1FB6C1] uppercase tracking-[0.15em] text-[10px] mb-1">
                    Phase {r2Result.phase} · {r2Result.mode || "verify"}
                  </div>
                  {r2Result.phase === 5 && (
                    <>
                      <div className="text-[#dde3ec] mb-2">
                        <strong className="text-white">{r2Result.total_touched}</strong> row(s){" "}
                        {r2Result.mode === "apply" ? "updated" : "planned for update"}.{" "}
                        Prefix: <code>{r2Result.prefix}</code>
                      </div>
                      <div className="text-[#9aa3b5] mb-2">{r2Result.advice}</div>
                      {Object.keys(r2Result.by_collection || {}).length > 0 && (
                        <table className="w-full text-[11px]">
                          <thead className="text-[#647087] uppercase text-[9px]">
                            <tr>
                              <th className="text-left py-0.5">Collection</th>
                              <th className="text-right">Scanned</th>
                              <th className="text-right">Touched</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(r2Result.by_collection).map(([k, v]) => (
                              <tr key={k} className={v.touched > 0 ? "text-amber-300" : "text-[#9aa3b5]"}>
                                <td>{k}</td>
                                <td className="text-right tabular-nums">{v.scanned}</td>
                                <td className="text-right tabular-nums">{v.touched}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </>
                  )}
                  {r2Result.phase === 6 && (
                    <>
                      <div className="mb-2 text-[#dde3ec]">
                        Bucket <code>{r2Result.bucket}</code> · prefix <code>{r2Result.prefix || "(none)"}</code>
                      </div>
                      <div className="flex gap-4 text-[#dde3ec] mb-2">
                        <span>resolved: <strong className="text-emerald-300">{r2Result.resolved}</strong></span>
                        <span>missing: <strong className={r2Result.missing === 0 ? "text-emerald-300" : "text-rose-300"}>{r2Result.missing}</strong></span>
                        <span>skipped (test/fake): <span className="text-[#647087]">{r2Result.skipped_test_keys}</span></span>
                      </div>
                      <div className={`mt-2 px-3 py-2 rounded ${r2Result.missing === 0 ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" : "bg-rose-500/10 border border-rose-500/30 text-rose-300"}`}>
                        {r2Result.advice}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </section>
      )}

      {/* Iter 419.p — Tenant-id backfill inline panel */}
      {backfillOpen && (
        <section
          className="mb-6 hv-card p-5 border-[#A78BFA]/25"
          data-testid="tenantid-backfill-panel"
        >
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-[#A78BFA]" />
            <h2 className="text-base font-semibold">Tenant-id backfill</h2>
            <button
              type="button"
              onClick={() => { setBackfillOpen(false); setBackfillResult(null); setBackfillConfirming(false); }}
              className="ml-auto hv-btn hv-btn-ghost text-xs"
              data-testid="tenantid-backfill-close"
            >
              Close
            </button>
          </div>
          <p className="text-sm text-[#a0aabf] mb-3">
            Stamps <code>tenant_id: &quot;hidroverse&quot;</code> on every legacy row that&apos;s currently
            missing one. <strong>This must be run before flipping <code>TENANT_DB_PER_TENANT=true</code></strong>
            — otherwise the strict tenant filter will hide untagged rows and they&apos;ll look deleted.
            Safe to re-run: never overwrites an existing tenant_id.
          </p>

          <div className="flex gap-2 flex-wrap mb-4">
            <button
              type="button"
              onClick={() => runBackfill(false)}
              disabled={backfillBusy}
              className="hv-btn hv-btn-ghost text-sm inline-flex items-center gap-1.5"
              data-testid="tenantid-backfill-dryrun"
            >
              {backfillBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              1. Dry-run report
            </button>
            {backfillResult && !backfillResult.error && backfillResult.mode === "dry_run" && backfillResult.totals.untagged > 0 && (
              backfillConfirming ? (
                <>
                  <button
                    type="button"
                    onClick={() => runBackfill(true)}
                    disabled={backfillBusy}
                    className="hv-btn text-sm inline-flex items-center gap-1.5 bg-emerald-500/80 hover:bg-emerald-500 text-white border border-emerald-500/60"
                    data-testid="tenantid-backfill-confirm"
                  >
                    {backfillBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
                    Confirm — stamp {backfillResult.totals.untagged.toLocaleString()} rows
                  </button>
                  <button
                    type="button"
                    onClick={() => setBackfillConfirming(false)}
                    className="hv-btn hv-btn-ghost text-sm"
                    data-testid="tenantid-backfill-cancel"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setBackfillConfirming(true)}
                  disabled={backfillBusy}
                  className="hv-btn hv-btn-primary text-sm inline-flex items-center gap-1.5"
                  data-testid="tenantid-backfill-apply"
                >
                  <Shield className="w-3.5 h-3.5" />
                  2. Apply (write to DB)
                </button>
              )
            )}
          </div>

          {backfillResult?.error && (
            <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2 inline-flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> {backfillResult.error}
            </div>
          )}

          {backfillResult && !backfillResult.error && (
            <div className="text-xs bg-[#0a1628] border border-white/10 rounded p-3">
              <div className="mb-2 text-[#dde3ec] flex items-center gap-3 flex-wrap">
                <span className={`inline-block px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${backfillResult.mode === "apply" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>
                  {backfillResult.mode === "apply" ? "Applied" : "Dry-run"}
                </span>
                <span>Untagged: <strong className="text-amber-300 tabular-nums">{backfillResult.totals.untagged.toLocaleString()}</strong></span>
                {backfillResult.mode === "apply" && (
                  <span>Patched: <strong className="text-emerald-300 tabular-nums">{backfillResult.totals.patched.toLocaleString()}</strong></span>
                )}
                <span>Collections: <strong>{backfillResult.totals.collections_affected}</strong></span>
              </div>
              {backfillResult.collections.length > 0 ? (
                <table className="w-full text-xs">
                  <thead className="text-[#647087] uppercase tracking-wider">
                    <tr>
                      <th className="text-left py-0.5">Collection</th>
                      <th className="text-right">Total</th>
                      <th className="text-right">Untagged</th>
                      {backfillResult.mode === "apply" && <th className="text-right">Patched</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {backfillResult.collections.map((r) => (
                      <tr key={r.collection} className={r.untagged > 0 ? "text-amber-300" : "text-[#9aa3b5]"}>
                        <td className="py-0.5">{r.collection}</td>
                        <td className="text-right tabular-nums">{r.total.toLocaleString()}</td>
                        <td className="text-right tabular-nums font-bold">{r.untagged.toLocaleString()}</td>
                        {backfillResult.mode === "apply" && (
                          <td className="text-right tabular-nums text-emerald-300">{r.patched.toLocaleString()}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-emerald-300 flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5" /> No untagged rows found — you&apos;re ready to flip the flag.
                </div>
              )}
              {backfillResult.skipped_huge?.length > 0 && (
                <div className="mt-2 text-[10px] text-[#647087]">
                  Skipped huge collections (&gt;100k rows): {backfillResult.skipped_huge.map((s) => s.collection).join(", ")}
                </div>
              )}
              {backfillResult.mode === "apply" && (
                <div className="mt-3 px-3 py-2 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs">
                  ✓ Backfill complete. Re-run the dry-run report to confirm 0 untagged rows remain before flipping <code>TENANT_DB_PER_TENANT=true</code>.
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Iter 419.r — Tenant reclaim (per-tenant DB copy) inline panel */}
      {reclaimOpen && (
        <section
          className="mb-6 hv-card p-5 border-[#1FB6C1]/25"
          data-testid="tenant-reclaim-panel"
        >
          <div className="flex items-center gap-2 mb-3">
            <Database className="w-4 h-4 text-[#1FB6C1]" />
            <h2 className="text-base font-semibold">Tenant DB reclaim (per-tenant copy)</h2>
            <button
              type="button"
              onClick={() => { setReclaimOpen(false); setReclaimResult(null); setReclaimConfirming(false); }}
              className="ml-auto hv-btn hv-btn-ghost text-xs"
              data-testid="tenant-reclaim-close"
            >
              Close
            </button>
          </div>
          <p className="text-sm text-[#a0aabf] mb-3">
            Copies each tenant&apos;s rows from the shared legacy DB into
            its own Mongo database (<code>tenant_hidroverse</code>, <code>tenant_demo</code>, …).
            <strong> Run this AFTER the tenant-id backfill and BEFORE flipping <code>TENANT_DB_PER_TENANT=true</code></strong>
            — otherwise the app reads from empty per-tenant DBs and users see missing data.
            Safe to re-run: never overwrites, skips already-copied rows by id, never deletes from legacy.
          </p>

          <div className="flex gap-2 flex-wrap mb-4">
            <button
              type="button"
              onClick={() => runReclaim(false)}
              disabled={reclaimBusy}
              className="hv-btn hv-btn-ghost text-sm inline-flex items-center gap-1.5"
              data-testid="tenant-reclaim-dryrun"
            >
              {reclaimBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              1. Dry-run report
            </button>
            {reclaimResult && !reclaimResult.error && reclaimResult.mode === "dry_run" && reclaimResult.totals.source > 0 && (
              reclaimConfirming ? (
                <>
                  <button
                    type="button"
                    onClick={() => runReclaim(true)}
                    disabled={reclaimBusy}
                    className="hv-btn text-sm inline-flex items-center gap-1.5 bg-emerald-500/80 hover:bg-emerald-500 text-white border border-emerald-500/60"
                    data-testid="tenant-reclaim-confirm"
                  >
                    {reclaimBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Database className="w-3.5 h-3.5" />}
                    Confirm — copy {reclaimResult.totals.source.toLocaleString()} rows
                  </button>
                  <button
                    type="button"
                    onClick={() => setReclaimConfirming(false)}
                    className="hv-btn hv-btn-ghost text-sm"
                    data-testid="tenant-reclaim-cancel"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setReclaimConfirming(true)}
                  disabled={reclaimBusy}
                  className="hv-btn hv-btn-primary text-sm inline-flex items-center gap-1.5"
                  data-testid="tenant-reclaim-apply"
                >
                  <Database className="w-3.5 h-3.5" />
                  2. Apply (copy into per-tenant DBs)
                </button>
              )
            )}
          </div>

          {reclaimResult?.error && (
            <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2 inline-flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> {reclaimResult.error}
            </div>
          )}

          {reclaimResult && !reclaimResult.error && (
            <div className="text-xs bg-[#0a1628] border border-white/10 rounded p-3 space-y-3">
              <div className="text-[#dde3ec] flex items-center gap-3 flex-wrap">
                <span className={`inline-block px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${reclaimResult.mode === "apply" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>
                  {reclaimResult.mode === "apply" ? "Applied" : "Dry-run"}
                </span>
                <span>Source rows: <strong className="text-amber-300 tabular-nums">{reclaimResult.totals.source.toLocaleString()}</strong></span>
                {reclaimResult.mode === "apply" && (
                  <span>Copied: <strong className="text-emerald-300 tabular-nums">{reclaimResult.totals.copied.toLocaleString()}</strong></span>
                )}
                <span>Tenants: <strong>{reclaimResult.tenants.length}</strong></span>
              </div>
              {reclaimResult.tenants.map((t) => (
                <div key={t.tenant_id} className="border border-white/5 rounded p-2 bg-white/[0.02]">
                  <div className="flex items-center gap-2 mb-1.5">
                    <strong className="text-[#1FB6C1]">{t.tenant_id}</strong>
                    <span className="text-[10px] text-[#647087]">→ {t.target_db}</span>
                    <span className="ml-auto text-[10px] text-[#dde3ec]">
                      {reclaimResult.mode === "apply"
                        ? <>copied <strong className="text-emerald-300">{t.totals.copied.toLocaleString()}</strong> of {t.totals.source.toLocaleString()}</>
                        : <>would copy <strong className="text-amber-300">{t.totals.source.toLocaleString()}</strong></>}
                    </span>
                  </div>
                  {t.collections.length > 0 && (
                    <table className="w-full text-[10px]">
                      <thead className="text-[#647087] uppercase tracking-wider">
                        <tr>
                          <th className="text-left py-0.5">Collection</th>
                          <th className="text-right">Source</th>
                          <th className="text-right">Already in target</th>
                          {reclaimResult.mode === "apply" && <th className="text-right">Copied</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {t.collections.map((c) => (
                          <tr key={c.collection} className="text-[#9aa3b5]">
                            <td className="py-0.5">{c.collection}</td>
                            <td className="text-right tabular-nums">{c.source_count.toLocaleString()}</td>
                            <td className="text-right tabular-nums">{c.already_in_target.toLocaleString()}</td>
                            {reclaimResult.mode === "apply" && (
                              <td className="text-right tabular-nums text-emerald-300">{c.copied.toLocaleString()}</td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
              {reclaimResult.mode === "apply" && (
                <div className="mt-1 px-3 py-2 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs">
                  ✓ Reclaim complete. Now go to the Emergent Secrets panel:
                  <ol className="mt-1 ml-4 list-decimal">
                    <li>Add <code>STRICT_TENANT_SCOPE</code> = <code>true</code></li>
                    <li>Change <code>TENANT_DB_PER_TENANT</code> from <code>false</code> to <code>true</code></li>
                    <li>Re-publish</li>
                  </ol>
                  Then smoke-test by logging in as any user — data should look identical.
                </div>
              )}
            </div>
          )}
        </section>
      )}



      {/* Stage 2.5 — Reset demo data inline panel */}
      {demoOpen && (
        <section
          className="mb-6 hv-card p-5 border-[#1FB6C1]/20"
          data-testid="reset-demo-panel"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold inline-flex items-center gap-2">
              <Repeat className="w-4 h-4 text-[#1FB6C1]" /> Demo tenant data
            </h2>
            <button
              type="button"
              onClick={() => setDemoOpen(false)}
              className="text-xs text-[#9aa3b5] hover:text-white"
              data-testid="reset-demo-close"
            >
              Hide
            </button>
          </div>
          <p className="text-xs text-[#9aa3b5] mb-3">
            Wipes the <code>demo</code> tenant&apos;s business data and rebuilds:
            15 customers · 39 sites · 80 leads · 120 JCs · 60 sales docs · 40 inventory ·
            30 candidates · 80 calendar events · 8 chat threads · 10 projects · 40 meetings.
            Production-safe — scoped strictly to <code>tenant_id=&quot;demo&quot;</code>.
          </p>
          <button
            type="button"
            onClick={resetDemo}
            disabled={demoBusy}
            className="hv-btn hv-btn-primary text-xs inline-flex items-center gap-1"
            data-testid="reset-demo-confirm"
          >
            {demoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Repeat className="w-3.5 h-3.5" />}
            Re-seed demo
          </button>
          <button
            type="button"
            onClick={ensureDemoBranding}
            disabled={demoBusy}
            className="ml-2 hv-btn hv-btn-ghost text-xs inline-flex items-center gap-1"
            data-testid="ensure-demo-branding"
            title="Force demo's branding back to public Rogue/Geminii brand (no data wipe)"
          >
            {demoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Palette className="w-3.5 h-3.5" />}
            Fix demo branding (Rogue)
          </button>
          {demoStatus && (
            <div className="mt-3 text-xs text-[#dde3ec] bg-black/30 border border-white/5 rounded px-3 py-2" data-testid="reset-demo-status">
              {demoStatus}
            </div>
          )}
        </section>
      )}

      <div className="relative max-w-md mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#647087]" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search by name, slug, or owner email…"
          className="hv-input pl-9 w-full"
          data-testid="admin-search"
        />
      </div>

      {err && (
        <div className="mb-4 px-3 py-2 rounded bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm inline-flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {err}
        </div>
      )}

      {filtered.length === 0 && !busy && (
        <div className="hv-card p-10 text-center text-[#9aa3b5]">No tenants found.</div>
      )}

      <div className="space-y-2">
        {filtered.map((t) => (
          <div
            key={t.id}
            className="hv-card p-4 hover:border-[#1FB6C1]/30 transition-colors"
            data-testid={`tenant-row-${t.id}`}
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-base font-semibold">{t.name || t.id}</div>
                  <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded ${STATUS_TINT[t.status] || "bg-white/[0.04] text-[#647087]"}`}>
                    {t.status || "unknown"}
                  </span>
                  {t.plan && (
                    <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded bg-white/[0.04] text-[#dde3ec]">
                      {t.plan}
                    </span>
                  )}
                  {(t.custom_domains || []).length > 0 && (
                    <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded bg-[#1FB6C1]/15 text-[#1FB6C1] inline-flex items-center gap-1">
                      <Globe className="w-3 h-3" />
                      {(t.custom_domains || []).length} domain{(t.custom_domains || []).length > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#9aa3b5] mt-1 flex flex-wrap gap-x-3 gap-y-1">
                  <span>id: <code className="text-[#cbd5e1]">{t.id}</code></span>
                  {t.owner_email && <span>owner: {t.owner_email}</span>}
                  {t.trial_ends_at && t.status === "trial" && (
                    <span>trial ends: {new Date(t.trial_ends_at).toLocaleDateString()}</span>
                  )}
                </div>
                {t.counts && (
                  <div className="text-xs text-[#647087] mt-1.5 flex gap-3">
                    <span>{t.counts.users || 0} users</span>
                    <span>{(t.counts.customers || 0).toLocaleString()} customers</span>
                    <span>{(t.counts.jobcards || 0).toLocaleString()} job-cards</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => toggleDomains(t.id)}
                  className="hv-btn hv-btn-ghost text-xs inline-flex items-center gap-1"
                  data-testid={`tenant-domains-toggle-${t.id}`}
                  title="Manage custom domains for this tenant"
                >
                  <Globe className="w-3.5 h-3.5" /> Domains
                  {expandedDomains[t.id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                </button>
                {t.status === "suspended" ? (
                  <button
                    type="button"
                    onClick={() => resume(t.id)}
                    className="hv-btn hv-btn-ghost text-xs inline-flex items-center gap-1"
                    data-testid={`tenant-resume-${t.id}`}
                  >
                    <PlayCircle className="w-3.5 h-3.5" /> Resume
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => suspend(t.id)}
                    className="hv-btn hv-btn-ghost text-xs inline-flex items-center gap-1"
                    data-testid={`tenant-suspend-${t.id}`}
                  >
                    <PauseCircle className="w-3.5 h-3.5" /> Suspend
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => impersonate(t.id)}
                  className="hv-btn hv-btn-ghost text-xs inline-flex items-center gap-1"
                  data-testid={`tenant-impersonate-${t.id}`}
                >
                  <UserCheck className="w-3.5 h-3.5" /> Impersonate
                </button>
              </div>
            </div>
            {expandedDomains[t.id] && (
              <DomainsEditor
                tenant={t}
                onUpdated={updateTenantInline}
                tenantSuffix={domainCfg?.tenant_suffix}
                scheme={domainCfg?.scheme}
                baseDomain={domainCfg?.base_domain}
                productLine={domainCfg?.product_line}
              />
            )}
          </div>
        ))}
      </div>

      {/* Audit log drawer */}
      {auditOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60" onClick={() => setAuditOpen(false)}>
          <div className="bg-[#121419] border border-white/10 rounded-t-xl sm:rounded-xl w-full sm:w-2xl max-h-[80vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold inline-flex items-center gap-2">
                <ScrollText className="w-4 h-4 text-[#1FB6C1]" /> Audit log
              </h2>
              <button type="button" onClick={() => setAuditOpen(false)} className="text-xs text-[#9aa3b5] hover:text-white">
                Close
              </button>
            </div>
            {audit === null ? (
              <Loader2 className="w-5 h-5 animate-spin text-[#1FB6C1]" />
            ) : audit.length === 0 ? (
              <div className="text-sm text-[#9aa3b5]">No audit events yet.</div>
            ) : (
              <ul className="space-y-2 text-xs">
                {audit.map((r, i) => (
                  <li key={i} className="border-l-2 border-[#1FB6C1]/40 pl-3 py-1">
                    <div className="text-white">
                      <span className="uppercase tracking-[0.15em] text-[10px] text-[#1FB6C1]">{r.kind}</span>
                      <span className="ml-2 text-[#dde3ec]">{r.tenant_id}</span>
                    </div>
                    <div className="text-[#9aa3b5]">
                      by {r.admin_email} · {new Date(r.at).toLocaleString()}
                      {r.target_email && <span> → {r.target_email}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* DB health & cutover-readiness drawer */}
      {healthOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60" onClick={() => setHealthOpen(false)}>
          <div className="bg-[#121419] border border-white/10 rounded-t-xl sm:rounded-xl w-full sm:max-w-3xl max-h-[85vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="db-health-panel">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold inline-flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#1FB6C1]" /> Per-tenant DB health & migration parity
              </h2>
              <button type="button" onClick={() => setHealthOpen(false)} className="text-xs text-[#9aa3b5] hover:text-white" data-testid="db-health-close">
                Close
              </button>
            </div>

            {healthBusy && (
              <div className="text-sm text-[#9aa3b5] inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Probing every tenant database…
              </div>
            )}

            {!healthBusy && health && (
              <section className="mb-6">
                <div className="text-xs uppercase tracking-[0.18em] text-[#1FB6C1] mb-2">Connectivity</div>
                <div className="text-xs text-[#9aa3b5] mb-3">
                  Flag <code className="text-[#dde3ec]">TENANT_DB_PER_TENANT</code> ={" "}
                  <code className={health.per_tenant_enabled ? "text-emerald-300" : "text-amber-300"}>
                    {String(health.per_tenant_enabled)}
                  </code>{" "}
                  · Legacy DB: <code className="text-[#dde3ec]">{health.legacy_db}</code> · Control plane:{" "}
                  <code className="text-[#dde3ec]">{health.control_plane_db}</code>
                </div>
                <table className="w-full text-xs">
                  <thead className="text-[#647087] uppercase tracking-[0.15em] text-[10px]">
                    <tr><th className="text-left py-1">Database</th><th className="text-left">Label</th><th className="text-right">Status</th></tr>
                  </thead>
                  <tbody>
                    {(health.probes || []).map((p, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="py-1.5"><code className="text-[#dde3ec]">{p.db}</code></td>
                        <td className="text-[#9aa3b5]">{p.label}</td>
                        <td className="text-right">
                          {p.ok ? (
                            <span className="text-emerald-300">✓ reachable{p.users !== undefined ? ` (${p.users} users)` : p.tenants !== undefined ? ` (${p.tenants} tenants)` : ""}</span>
                          ) : (
                            <span className="text-rose-300" title={p.error}>✗ {p.error?.slice(0, 60)}…</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {health.summary && health.summary.failing.length > 0 && (
                  <div className="mt-3 px-3 py-2 rounded bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs">
                    <strong>{health.summary.failing.length} database(s) unreachable.</strong>{" "}
                    Grant the production Mongo user <code>readWrite</code> on:{" "}
                    {health.summary.failing.map((d) => <code key={d} className="mx-1 text-rose-200">{d}</code>)}
                  </div>
                )}
              </section>
            )}

            {!healthBusy && parity && (
              <section>
                <div className="text-xs uppercase tracking-[0.18em] text-[#1FB6C1] mb-2">Migration parity (legacy → per-tenant)</div>
                <div className={`px-3 py-2 rounded text-xs mb-3 ${parity.ready_to_cutover ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" : "bg-amber-500/10 border border-amber-500/30 text-amber-300"}`}>
                  {parity.advice}
                </div>
                {parity.tenants.map((t) => (
                  <details key={t.tenant_id} className="mb-2 border border-white/5 rounded">
                    <summary className="cursor-pointer px-3 py-2 text-xs flex items-center justify-between">
                      <span>
                        <code className="text-[#dde3ec]">{t.tenant_id}</code>{" "}
                        <span className="text-[#9aa3b5]">— {t.tenant_name}</span>
                      </span>
                      <span className={t.clean ? "text-emerald-300" : "text-amber-300"}>
                        {t.clean ? "✓ clean" : "✗ diffs"}
                      </span>
                    </summary>
                    <div className="px-3 py-2 max-h-64 overflow-auto">
                      <table className="w-full text-[11px]">
                        <thead className="text-[#647087] uppercase text-[9px]">
                          <tr><th className="text-left py-0.5">Collection</th><th className="text-right">Legacy</th><th className="text-right">Tenant DB</th><th className="text-right">Diff</th></tr>
                        </thead>
                        <tbody>
                          {(t.collections || []).map((c, i) => (
                            <tr key={i} className={c.blocks_cutover ? "text-rose-300" : c.diff > 0 ? "text-amber-300" : "text-[#9aa3b5]"}>
                              <td className="py-0.5">{c.collection}</td>
                              <td className="text-right tabular-nums">{c.legacy_count?.toLocaleString() ?? "—"}</td>
                              <td className="text-right tabular-nums">{c.tenant_db_count?.toLocaleString() ?? "—"}</td>
                              <td className="text-right tabular-nums">{c.diff > 0 ? "+" : ""}{c.diff}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                ))}
              </section>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
